/**
 * Onglet « Style » du builder, monté par type de widget.
 *
 * Piloté par `widgetStyleCaps` : chaque type n'expose que les sections pertinentes.
 * Tous les contrôles écrivent sous `config.display.*` (deep-mergé par `deepMergeConfig`),
 * sauf la couleur principale qui vit sous `config.format.color`. L'aperçu du canvas se
 * met à jour en direct.
 *
 * Réorganisation 2026-08-06 : 10 sections historiques (accumulées PR après PR) fusionnées
 * en 5 par intention (Cadre/Couleurs/Rendu/Métriques/Texte), sans toucher aux 12 flags de
 * `widgetStyleCaps` — seul le regroupement JSX change. cf. spec
 * `context/specs/2026-08-06-styletab-refonte-densite.md`.
 */
import type { Widget, WidgetConfig, WidgetDisplay, CompareFormat } from '../schemas';
import { STYLE_CAPS } from './widgetStyleCaps';
import { ConfigSection } from './ConfigSection';
import { Toggle, ColorField, SelectField, AlignPicker, SizeSelect, TextField } from './controls';
type MetricAlign = 'left' | 'center' | 'right';
import StylePresetPicker from './StylePresetPicker';
import IconPicker from './IconPicker';
import PalettePicker from './PalettePicker';
import { resolveWidgetPalette } from '../chartPalette';
import { useChartPalette } from '../ChartPaletteContext';

interface StyleTabProps {
  widget: Widget;
  cfg: WidgetConfig;
  patchConfig: (partial: Partial<WidgetConfig>) => void;
  /** Libellé FR d'une métrique (section Couleurs/Métriques). Repli sur la clé si absent. */
  metricLabel?: (key: string) => string;
}

const LEGEND_POSITION_OPTIONS = [
  { value: 'bottom', label: 'Bas' },
  { value: 'top', label: 'Haut' },
  { value: 'left', label: 'Gauche' },
  { value: 'right', label: 'Droite' },
];

const Y_AXIS_SCALE_OPTIONS = [
  { value: 'linear', label: 'Linéaire' },
  { value: 'log', label: 'Logarithmique' },
];

const CHART_DISPLAY: Partial<
  Record<
    Widget['type'],
    { legend?: boolean; grid?: boolean; yAxis?: boolean; smooth?: boolean; labels?: boolean; horizontal?: boolean }
  >
> = {
  line_chart: { legend: true, grid: true, yAxis: true, smooth: true },
  bar_chart: { legend: true, grid: true, yAxis: true, horizontal: true },
  area_chart: { legend: true, grid: true, yAxis: true, smooth: true },
  donut_pie: { legend: true, labels: true },
};

const BORDER_OPTIONS = [
  { value: 'none', label: 'Aucune' },
  { value: 'full', label: 'Complète' },
  { value: 'left', label: 'Gauche' },
  { value: 'top', label: 'Haut' },
];

const FONT_OPTIONS = [
  { value: 'sans', label: 'Sans-serif' },
  { value: 'serif', label: 'Serif' },
  { value: 'mono', label: 'Monospace' },
];

const TITLE_SIZE_OPTIONS = [
  { value: 'xs', label: 'Petit' },
  { value: 'sm', label: 'Moyen' },
  { value: 'base', label: 'Grand' },
];

const VALUE_SIZE_OPTIONS = [
  { value: 'xl', label: 'XL' },
  { value: '2xl', label: '2XL' },
  { value: '3xl', label: '3XL' },
  { value: '4xl', label: '4XL' },
  { value: '5xl', label: '5XL' },
];

const TREND_OPTIONS = [
  { value: 'up', label: 'La hausse est positive' },
  { value: 'down', label: 'La baisse est positive' },
];

const COMPARE_FORMAT_OPTIONS = [
  { value: 'percent', label: 'Pourcentage' },
  { value: 'absolute', label: 'Valeur seule' },
  { value: 'both', label: 'Valeur + %' },
  { value: 'diff', label: 'Différence absolue' },
];

const DENSITY_OPTIONS = [
  { value: 'compact', label: 'Compact' },
  { value: 'normal', label: 'Normal' },
];

export default function StyleTab({ widget, cfg, patchConfig, metricLabel }: StyleTabProps) {
  const caps = STYLE_CAPS[widget.type];
  const d: WidgetDisplay = cfg.display ?? {};
  const dc = CHART_DISPLAY[widget.type] ?? {};
  const metrics = cfg.metrics ?? [];
  const themePalette = useChartPalette();
  const widgetPalette = resolveWidgetPalette(
    cfg.format?.palette as string | string[] | undefined,
    themePalette,
  );
  const showPalette =
    caps.palette && (widget.type === 'donut_pie' || widget.type === 'treemap' || metrics.length > 1);
  const seriesColors = (cfg.format?.series_colors as Record<string, string> | undefined) ?? {};
  const yAxisAssign = (d.series_y_axis as Record<string, 0 | 1> | undefined) ?? {};
  const setAxis = (m: string, axis: 0 | 1) =>
    patchDisplay({ series_y_axis: { ...yAxisAssign, [m]: axis } });
  const isChartMultiSeries = caps.series && metrics.length > 1;
  const showColorsSection = showPalette || isChartMultiSeries || (caps.color && !isChartMultiSeries);
  const showRenduSection = caps.display || caps.tableOptions;
  const showTexteSection = caps.alignment || caps.typography;
  const showMetriquesSection = (caps.metrics && metrics.length > 0) || caps.trend;

  const patchDisplay = (partial: Partial<WidgetDisplay>) => patchConfig({ display: partial });

  /** Clé localStorage stable par (type de widget, section) — persistance du replié/déplié. */
  const sectionKey = (slug: string) => `orion.style-sections.${widget.type}.${slug}`;

  const texteModified = Boolean(
    d.font_family || d.title_size || d.title_color || d.title_italic || d.title_underline ||
    d.value_size || d.align_title || d.align_value || d.align_comparison,
  );

  const hasAny =
    caps.appearance || caps.color || caps.typography || caps.alignment || caps.trend || caps.tableOptions;

  if (!hasAny) {
    return (
      <div className="py-2 text-sm italic text-[#8B8FA7]">
        Aucune option de style pour ce widget.
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {caps.appearance && (
        <ConfigSection title="Cadre" storageKey={sectionKey('cadre')}>
          <StylePresetPicker value={d.preset} onSelect={patchConfig} />
          <ColorField label="Fond" value={d.bg} onChange={(bg) => patchDisplay({ bg })} />
          <SelectField
            label="Bordure"
            value={d.border ?? 'none'}
            options={BORDER_OPTIONS}
            onChange={(border) => patchDisplay({ border: border as WidgetDisplay['border'] })}
          />
          {d.border && d.border !== 'none' && (
            <ColorField
              label="Couleur de bordure"
              value={d.border_color}
              onChange={(border_color) => patchDisplay({ border_color })}
            />
          )}
          <Toggle
            label="Ombre portée"
            checked={d.shadow ?? false}
            onChange={(shadow) => patchDisplay({ shadow })}
          />
          <IconPicker value={d.icon} onChange={(icon) => patchDisplay({ icon })} />
          {d.icon && (
            <ColorField
              label="Couleur de l'icône"
              value={d.icon_color}
              onChange={(icon_color) => patchDisplay({ icon_color })}
            />
          )}
          <TextField
            label="Badge"
            value={d.badge ?? ''}
            placeholder="ex. Insight"
            onChange={(v) => patchDisplay({ badge: v || undefined })}
          />
        </ConfigSection>
      )}

      {showColorsSection && (
        <ConfigSection title="Couleurs" storageKey={sectionKey('couleurs')}>
          {showPalette && (
            <PalettePicker
              value={cfg.format?.palette as string | string[] | undefined}
              onChange={(palette) => patchConfig({ format: { palette } })}
            />
          )}
          {isChartMultiSeries && (
            <div className="flex flex-col gap-1">
              {metrics.map((m, i) => {
                const color = seriesColors[m] ?? widgetPalette[i % widgetPalette.length];
                const name = metricLabel ? metricLabel(m) : m;
                return (
                  <div key={m} className="flex min-h-[28px] items-center gap-2">
                    <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
                      <span className="relative h-6 w-6 shrink-0">
                        <span className="block h-6 w-6 rounded" style={{ backgroundColor: color }} />
                        <input
                          type="color"
                          value={color}
                          onChange={(e) =>
                            patchConfig({
                              format: { series_colors: { ...seriesColors, [m]: e.target.value } as Record<string, string> },
                            })
                          }
                          className="absolute inset-0 h-6 w-6 cursor-pointer opacity-0"
                        />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-xs text-[#1B2559]">{name}</span>
                    </label>
                    <div className="flex shrink-0 overflow-hidden rounded-md border border-[#E2E4ED]">
                      {([
                        { v: 0 as const, label: 'G', title: 'Axe gauche' },
                        { v: 1 as const, label: 'D', title: 'Axe droit' },
                      ]).map((opt) => {
                        const active = (yAxisAssign[m] ?? 0) === opt.v;
                        return (
                          <button
                            key={opt.v}
                            type="button"
                            aria-pressed={active}
                            aria-label={opt.title}
                            title={opt.title}
                            onClick={() => setAxis(m, opt.v)}
                            className={`h-5 w-5 text-[10px] font-semibold transition-colors ${
                              active ? 'bg-[#F4F5FA] text-[#6366F1]' : 'bg-white text-[#8B8FA7] hover:bg-[#F4F5FA]'
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {caps.color && !isChartMultiSeries && (
            <ColorField
              label="Couleur principale"
              value={cfg.format?.color as string | undefined}
              onChange={(color) => patchConfig({ format: { color } })}
            />
          )}
        </ConfigSection>
      )}

      {showRenduSection && (
        <ConfigSection title="Rendu" storageKey={sectionKey('rendu')}>
          {dc.legend && (
            <>
              <Toggle
                label="Légende"
                checked={d.show_legend ?? metrics.length > 1}
                onChange={(show_legend) => patchDisplay({ show_legend })}
              />
              <SelectField
                label="Position de la légende"
                value={(d.legend_position as string | undefined) ?? 'bottom'}
                options={LEGEND_POSITION_OPTIONS}
                onChange={(legend_position) => patchDisplay({ legend_position })}
              />
            </>
          )}
          {dc.grid && (
            <Toggle
              label="Grille"
              checked={d.show_grid ?? true}
              onChange={(show_grid) => patchDisplay({ show_grid })}
            />
          )}
          {dc.yAxis && (
            <>
              <Toggle
                label="Axe des valeurs"
                checked={d.show_y_axis ?? true}
                onChange={(show_y_axis) => patchDisplay({ show_y_axis })}
              />
              <SelectField
                label="Échelle"
                value={(d.y_axis_scale as string | undefined) ?? 'linear'}
                options={Y_AXIS_SCALE_OPTIONS}
                onChange={(y_axis_scale) =>
                  patchDisplay({ y_axis_scale: y_axis_scale as 'linear' | 'log' })
                }
              />
            </>
          )}
          {dc.smooth && (
            <Toggle
              label="Courbe lissée"
              checked={d.smooth ?? true}
              onChange={(smooth) => patchDisplay({ smooth })}
            />
          )}
          {dc.horizontal && (
            <Toggle
              label="Barres horizontales"
              checked={d.horizontal ?? false}
              onChange={(horizontal) => patchDisplay({ horizontal })}
            />
          )}
          {dc.labels && (
            <Toggle
              label="Étiquettes de données"
              checked={d.show_labels ?? false}
              onChange={(show_labels) => patchDisplay({ show_labels })}
            />
          )}
          {caps.tableOptions && (
            <>
              <Toggle
                label="Ligne de total"
                checked={d.show_total ?? false}
                onChange={(show_total) => patchDisplay({ show_total })}
              />
              <Toggle
                label="En-têtes en gras"
                checked={d.bold_header ?? true}
                onChange={(bold_header) => patchDisplay({ bold_header })}
              />
              {(d.show_total ?? false) && (
                <Toggle
                  label="Total en gras"
                  checked={d.bold_total ?? true}
                  onChange={(bold_total) => patchDisplay({ bold_total })}
                />
              )}
              <Toggle
                label="1re colonne en gras"
                checked={d.bold_first_col ?? false}
                onChange={(bold_first_col) => patchDisplay({ bold_first_col })}
              />
              <Toggle
                label="Lignes alternées"
                checked={d.striped ?? false}
                onChange={(striped) => patchDisplay({ striped })}
              />
              <SelectField
                label="Densité"
                value={d.density ?? 'normal'}
                options={DENSITY_OPTIONS}
                onChange={(density) => patchDisplay({ density: density as WidgetDisplay['density'] })}
              />
              <AlignPicker
                label="Alignement des métriques"
                value={(d.metric_align as MetricAlign | undefined) ?? 'right'}
                onChange={(metric_align) => patchDisplay({ metric_align })}
              />
            </>
          )}
        </ConfigSection>
      )}

      {showMetriquesSection && (
        <ConfigSection title="Métriques" storageKey={sectionKey('metriques')}>
          {metrics.map((m) => {
            const labels = (cfg.metric_labels as Record<string, string> | undefined) ?? {};
            const formats = (cfg.metric_compare_format as Record<string, CompareFormat> | undefined) ?? {};
            return (
              <div key={m} className="flex flex-col gap-1">
                <TextField
                  label={metricLabel ? metricLabel(m) : m}
                  value={labels[m] ?? ''}
                  placeholder={metricLabel ? metricLabel(m) : m}
                  onChange={(v) => {
                    const next: Record<string, string> = { ...labels };
                    if (v.trim()) next[m] = v;
                    else delete next[m];
                    patchConfig({ metric_labels: next });
                  }}
                />
                {caps.compareFormat && (
                  <SelectField
                    label="Comparaison"
                    value={formats[m] ?? 'percent'}
                    options={COMPARE_FORMAT_OPTIONS}
                    onChange={(fmt) =>
                      patchConfig({
                        metric_compare_format: { ...formats, [m]: fmt as CompareFormat },
                      })
                    }
                  />
                )}
                {caps.goodDirection && (
                  <SelectField
                    label="Sens favorable"
                    value={
                      ((cfg.metric_good_direction as Record<string, 'up' | 'down'> | undefined) ?? {})[m] ??
                      'up'
                    }
                    options={[
                      { value: 'up', label: 'Hausse = bien' },
                      { value: 'down', label: 'Baisse = bien' },
                    ]}
                    onChange={(dir) => {
                      const cur =
                        (cfg.metric_good_direction as Record<string, 'up' | 'down'> | undefined) ?? {};
                      patchConfig({
                        metric_good_direction: { ...cur, [m]: dir as 'up' | 'down' },
                      });
                    }}
                  />
                )}
              </div>
            );
          })}
          {caps.trend && (
            <SelectField
              label="Sens favorable"
              value={d.trend_is_good ?? 'up'}
              options={TREND_OPTIONS}
              onChange={(v) => patchDisplay({ trend_is_good: v as WidgetDisplay['trend_is_good'] })}
            />
          )}
        </ConfigSection>
      )}

      {showTexteSection && (
        <ConfigSection title="Texte" defaultOpen={false} storageKey={sectionKey('texte')} modified={texteModified}>
          {caps.alignment && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              <AlignPicker
                label="Titre"
                value={d.align_title ?? 'left'}
                onChange={(align_title) => patchDisplay({ align_title })}
              />
              <AlignPicker
                label="Valeur"
                value={d.align_value ?? 'left'}
                onChange={(align_value) => patchDisplay({ align_value })}
              />
            </div>
          )}
          {caps.alignment && widget.type === 'comparison_metric' && (
            <AlignPicker
              label="Comparaison"
              value={d.align_comparison ?? 'left'}
              onChange={(align_comparison) => patchDisplay({ align_comparison })}
            />
          )}
          {caps.typography && (
            <>
              <SelectField
                label="Police"
                value={d.font_family ?? 'sans'}
                options={FONT_OPTIONS}
                onChange={(font_family) =>
                  patchDisplay({ font_family: font_family as WidgetDisplay['font_family'] })
                }
              />
              <SizeSelect
                label="Taille du titre"
                value={d.title_size ?? 'sm'}
                options={TITLE_SIZE_OPTIONS}
                onChange={(title_size) =>
                  patchDisplay({ title_size: title_size as WidgetDisplay['title_size'] })
                }
              />
              <ColorField
                label="Couleur du titre"
                value={d.title_color}
                onChange={(title_color) => patchDisplay({ title_color })}
              />
              <Toggle
                label="Titre en italique"
                checked={d.title_italic ?? false}
                onChange={(title_italic) => patchDisplay({ title_italic })}
              />
              <Toggle
                label="Titre souligné"
                checked={d.title_underline ?? false}
                onChange={(title_underline) => patchDisplay({ title_underline })}
              />
              <SizeSelect
                label="Taille de la valeur"
                value={d.value_size ?? '2xl'}
                options={VALUE_SIZE_OPTIONS}
                onChange={(value_size) =>
                  patchDisplay({ value_size: value_size as WidgetDisplay['value_size'] })
                }
              />
            </>
          )}
        </ConfigSection>
      )}
    </div>
  );
}
